import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Building2,
    Code2,
    GraduationCap,
    Briefcase,
    Handshake,
    Sprout,
    HelpCircle,
    Mail,
    ArrowUpRight,
} from "lucide-react";

const DESTINATIONS = [
    { to: "/about", label: "About Us", blurb: "Who we are, our work, and the founder's story.", icon: Building2, testId: "dest-about" },
    { to: "/services", label: "Services", blurb: "Software, AI readiness and transparent pricing.", icon: Code2, testId: "dest-services" },
    { to: "/training", label: "Training & Internship", blurb: "Foundations, Pro and AI Builder tracks.", icon: GraduationCap, testId: "dest-training" },
    { to: "/careers", label: "Careers", blurb: "Paid internships and engineer roles at IndoBlitz.", icon: Briefcase, testId: "dest-careers" },
    { to: "/partner", label: "Partner", blurb: "Sell IndoBlitz in Sydney, Dubai and beyond.", icon: Handshake, testId: "dest-partner" },
    { to: "/csr", label: "CSR Activities", blurb: "Section 135-ready rural impact partnerships.", icon: Sprout, testId: "dest-csr" },
    { to: "/faq", label: "FAQ", blurb: "Pricing, delivery, CSR and how we work.", icon: HelpCircle, testId: "dest-faq" },
    { to: "/contact", label: "Contact", blurb: "Start a conversation. We reply in a day.", icon: Mail, testId: "dest-contact" },
];

const DestinationGrid = () => (
    <section id="destinations" className="relative py-14 lg:py-24 scroll-mt-20" data-testid="destination-grid">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
            <div className="mb-8 lg:mb-12">
                <div className="pill"><span className="accent-dot" />Where do you want to go?</div>
                <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl text-ivory tracking-tight">
                    Pick a door. <span className="italic text-gold-shimmer">We'll take it from there.</span>
                </h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                {DESTINATIONS.map((d, i) => {
                    const Icon = d.icon;
                    return (
                        <motion.div
                            key={d.to}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <Link
                                to={d.to}
                                className="glass glass-hover rounded-2xl p-5 lg:p-7 flex flex-col h-full min-h-[150px] lg:min-h-[190px] group"
                                data-testid={d.testId}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center group-hover:bg-gold/15 transition-colors">
                                        <Icon className="w-5 h-5 text-gold" />
                                    </div>
                                    <ArrowUpRight className="w-5 h-5 text-ivory/30 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                                </div>
                                <h3 className="mt-5 lg:mt-7 font-display text-xl lg:text-2xl text-ivory leading-tight">{d.label}</h3>
                                <p className="mt-2 text-[13px] lg:text-sm text-ivory/60 leading-snug">{d.blurb}</p>
                            </Link>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    </section>
);

export default DestinationGrid;
